/**
 * Server state for the LOI tab: the three layers in one query, polled only
 * while an extraction job is in flight (the server merges the job's state
 * into the head), and the writes. A 409 means the review was approved
 * elsewhere: reload and say so. A 422 carries the reasons the screen lists
 * inline. Everything else says what did not happen and offers Retry
 * (docs/ux/shell.md).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from '@/app/api';
import type { LoiDiffRow, LoiFieldDecisionInput, LoiFieldView, LoiHead, LoiQuestionDecisionInput, LoiQuestionView, LoiView } from '@/app/types';
import { detailKey, refreshAudit } from '../useMatter';

export const LOI_LOCKED_TEXT = 'This LOI was approved in another tab. Decisions are locked; nothing changed.';

/** After a 409 reload the locked sentence in the bar is where the reviewer should be. */
function focusStatus(): void {
  requestAnimationFrame(() => document.getElementById('loi-status')?.focus());
}

export function loiKey(ref: string) {
  return ['matter', ref, 'loi'] as const;
}
export function loiDiffKey(ref: string) {
  return ['matter', ref, 'loi', 'diff'] as const;
}

const base = (ref: string) => `/api/matters/${encodeURIComponent(ref)}/loi`;
export const letterPagesUrl = (ref: string, from: number, to: number) => `${base(ref)}/letter/pages/${from}/${to}`;

export const extracting = (head: LoiHead | undefined): boolean => head?.extract?.state === 'queued' || head?.extract?.state === 'running';

export function useLoi(ref: string, enabled = true) {
  return useQuery({
    queryKey: loiKey(ref),
    queryFn: () => api<LoiView>(base(ref)),
    enabled,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: 5_000,
    refetchInterval: (query) => (extracting(query.state.data?.head) ? 1_500 : false),
  });
}

export function useLoiDiff(ref: string, enabled: boolean) {
  return useQuery({
    queryKey: loiDiffKey(ref),
    queryFn: () => api<{ rows: LoiDiffRow[] }>(`${base(ref)}/diff`),
    select: (d) => d.rows,
    enabled,
    staleTime: 5_000,
  });
}

/** The reasons a 422 carried; null when the error was something else. */
export function reasonsOf(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 422 || !err.body || typeof err.body !== 'object') return null;
  const body = err.body as { reasons?: unknown; reason?: unknown };
  if (Array.isArray(body.reasons)) return body.reasons.filter((r): r is string => typeof r === 'string');
  if (typeof body.reason === 'string') return [body.reason];
  return null;
}

function failed(message: string, retry: () => void): void {
  toast.error(`${message} Nothing changed.`, { action: { label: 'Retry', onClick: retry } });
}

function useReload(ref: string) {
  const client = useQueryClient();
  return {
    client,
    reloadAll: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: loiKey(ref) }),
        client.invalidateQueries({ queryKey: detailKey(ref) }),
        refreshAudit(client, ref),
      ]);
    },
  };
}

export function useStartLoiReview(ref: string) {
  const { client, reloadAll } = useReload(ref);
  const mutation = useMutation({
    mutationFn: () => api<LoiHead>(`${base(ref)}/review/start`, { method: 'POST' }),
    onSuccess: (head) => {
      client.setQueryData<LoiView>(loiKey(ref), (prev) => (prev ? { ...prev, head: { ...prev.head, ...head } } : prev));
      void refreshAudit(client, ref);
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOI_LOCKED_TEXT); return; }
      failed('Could not start the review.', () => mutation.mutate());
    },
  });
  return mutation;
}

export type FieldVars = { key: string; input: LoiFieldDecisionInput; label: string };

export function useDecideField(ref: string, callbacks: { onRefused?: (key: string, reasons: string[]) => void } = {}) {
  const { client, reloadAll } = useReload(ref);
  const mutation = useMutation({
    mutationFn: (vars: FieldVars) => api<NonNullable<LoiFieldView['item']>>(`${base(ref)}/review/fields/${encodeURIComponent(vars.key)}`, { method: 'PUT', json: vars.input }),
    onSuccess: (item) => {
      client.setQueryData<LoiView>(loiKey(ref), (prev) => (prev ? { ...prev, fields: prev.fields.map((f) => (f.key === item.key ? { ...f, item } : f)) } : prev));
      void client.invalidateQueries({ queryKey: loiKey(ref) });
      void refreshAudit(client, ref);
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOI_LOCKED_TEXT); focusStatus(); return; }
      const reasons = reasonsOf(err);
      if (reasons) { callbacks.onRefused?.(vars.key, reasons); toast.error(`Could not save the decision for ${vars.label}. Nothing changed.`); return; }
      failed(`Could not save the decision for ${vars.label}.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export type QuestionVars = { id: string; input: LoiQuestionDecisionInput; label: string };

export function useDecideQuestion(ref: string, callbacks: { onRefused?: (id: string, reasons: string[]) => void } = {}) {
  const { client, reloadAll } = useReload(ref);
  const mutation = useMutation({
    mutationFn: (vars: QuestionVars) => api<NonNullable<LoiQuestionView['item']>>(`${base(ref)}/review/questions/${encodeURIComponent(vars.id)}`, { method: 'PUT', json: vars.input }),
    onSuccess: (item) => {
      client.setQueryData<LoiView>(loiKey(ref), (prev) => (prev ? { ...prev, questions: prev.questions.map((q) => (q.id === item.id ? { ...q, item } : q)) } : prev));
      void client.invalidateQueries({ queryKey: loiKey(ref) });
      void refreshAudit(client, ref);
    },
    onError: async (err, vars) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOI_LOCKED_TEXT); focusStatus(); return; }
      const reasons = reasonsOf(err);
      if (reasons) { callbacks.onRefused?.(vars.id, reasons); toast.error(`Could not save the decision for ${vars.label}. Nothing changed.`); return; }
      failed(`Could not save the decision for ${vars.label}.`, () => mutation.mutate(vars));
    },
  });
  return mutation;
}

export function useApproveLoi(ref: string, callbacks: { onRefused?: (reasons: string[]) => void; onApproved?: () => void } = {}) {
  const { client, reloadAll } = useReload(ref);
  const mutation = useMutation({
    mutationFn: () => api<{ head: LoiHead; authoritative: LoiView['authoritative'] }>(`${base(ref)}/approve`, { method: 'POST' }),
    onSuccess: async (data) => {
      client.setQueryData<LoiView>(loiKey(ref), (prev) => (prev ? { ...prev, head: { ...prev.head, ...data.head }, authoritative: data.authoritative, blocking: [] } : prev));
      await reloadAll();
      toast.success(`LOI approved. Authoritative form version ${data.head.authoritativeVersion} written. Decisions are locked.`);
      callbacks.onApproved?.();
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOI_LOCKED_TEXT); return; }
      const reasons = reasonsOf(err);
      if (reasons) { void client.invalidateQueries({ queryKey: loiKey(ref) }); callbacks.onRefused?.(reasons); toast.error('The form cannot be approved yet. Nothing changed.'); return; }
      failed('Could not approve the form.', () => mutation.mutate());
    },
  });
  return mutation;
}

export function useExtractLoi(ref: string) {
  const { client, reloadAll } = useReload(ref);
  const mutation = useMutation({
    mutationFn: () => api<{ jobId: string; state: string }>(`${base(ref)}/extract`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: loiKey(ref) });
      void client.invalidateQueries({ queryKey: detailKey(ref) });
    },
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 409) { await reloadAll(); toast.error(LOI_LOCKED_TEXT); return; }
      const reasons = reasonsOf(err);
      if (reasons) { toast.error(`Could not run the extraction: ${reasons[0]}. Nothing changed.`); return; }
      failed('Could not run the extraction.', () => mutation.mutate());
    },
  });
  return mutation;
}
